import type { CrmDashboard } from '../../api/types';
import { BarList, StatCard } from '../../components/DesignSystem';
import { formatCurrencyWhole, formatNumber, formatPercent } from '../../lib/format';
import { STAGE_LABELS } from './stages';

interface CrmDashboardTabProps {
  dashboard: CrmDashboard | undefined;
  isLoading: boolean;
}

/** CRM dashboard: headline stats, funnel, buyer types, exec performance. */
export function CrmDashboardTab({ dashboard, isLoading }: CrmDashboardTabProps) {
  if (isLoading) {
    return <p className="rfp-loading">Loading CRM dashboard…</p>;
  }

  return (
    <>
      <div className="rfp-stat-grid rfp-stat-grid--tight">
        <StatCard label="Customers" value={formatNumber(dashboard?.customers)} />
        <StatCard label="Sales Execs" value={formatNumber(dashboard?.salesExecs)} />
        <StatCard label="Active Deals" value={formatNumber(dashboard?.activeDeals)} />
        <StatCard
          label="Signed"
          value={
            <span className="rfp-value--green">{formatCurrencyWhole(dashboard?.signedTotal)}</span>
          }
        />
        <StatCard label="Pipeline" value={formatCurrencyWhole(dashboard?.pipelineTotal)} />
        <StatCard
          label="Win Rate"
          value={
            <span className="rfp-value--orange">{formatPercent(dashboard?.winRatePercent)}</span>
          }
        />
      </div>

      <section className="rfp-card">
        <div className="rfp-card__head">
          <h2 className="rfp-card__title">Pipeline Funnel</h2>
        </div>
        <BarList
          emptyMessage="No deals in the pipeline yet"
          formatValue={(value) => String(value)}
          items={(dashboard?.funnel ?? []).map((entry) => ({
            label: STAGE_LABELS[entry.stage],
            value: entry.count,
          }))}
          tone="primary"
        />
      </section>

      <div className="rfp-form-grid">
        <section className="rfp-card">
          <div className="rfp-card__head">
            <h2 className="rfp-card__title">Customers by Buyer Type</h2>
          </div>
          <BarList
            emptyMessage="No customers yet"
            items={(dashboard?.customersByBuyerType ?? []).map((entry) => ({
              label: entry.buyerType ?? 'Unspecified',
              value: entry.count,
            }))}
          />
        </section>

        <section className="rfp-card">
          <div className="rfp-card__head">
            <h2 className="rfp-card__title">Performance by Sales Executive</h2>
          </div>
          <BarList
            emptyMessage="No signed deals yet"
            formatValue={(value) => formatCurrencyWhole(value)}
            items={(dashboard?.performanceBySalesExec ?? []).map((entry) => ({
              label: entry.salesExecName,
              value: entry.signedTotal,
            }))}
          />
        </section>
      </div>
    </>
  );
}
